import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Download, LogOut, Mail, Pencil, Plus, Trash2, Users } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("intellix_token")}` };
}

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [tab, setTab] = useState("articles");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("intellix_token");
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .catch(() => { localStorage.removeItem("intellix_token"); navigate("/admin/login"); });
  }, [navigate]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/blog`),
      axios.get(`${API}/admin/messages`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/newsletter/subscribers`, { headers: authHeaders() }).catch(() => ({ data: [] })),
    ]).then(([blogRes, msgRes, subRes]) => {
      setPosts(blogRes.data);
      setMessages(msgRes.data);
      setSubscribers(subRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const logout = () => {
    localStorage.removeItem("intellix_token");
    localStorage.removeItem("intellix_user");
    navigate("/admin/login");
  };

  const deletePost = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { headers: authHeaders() });
      setPosts((p) => p.filter((post) => post.id !== id));
      toast.success("Article supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm("Désinscrire cet abonné ?")) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${id}`, { headers: authHeaders() });
      setSubscribers((s) => s.filter((sub) => sub.id !== id));
      toast.success("Abonné supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const exportSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/export`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "abonnes-newsletter.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  if (loading) {
    return <div className="flex min-h-[60vh] items-center justify-center pt-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="admin-dashboard-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">Panneau d'administration</p>
            <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Tableau de bord</h1>
          </div>
          <div className="flex gap-3">
            <Button asChild className="rounded-full px-6 font-semibold" data-testid="admin-new-article-btn">
              <Link to="/admin/blog/nouveau"><Plus className="h-4 w-4" /> Nouvel article</Link>
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Déconnexion
            </Button>
          </div>
        </div>

        <div className="mt-8 flex gap-2">
          <Button variant={tab === "articles" ? "default" : "outline"} onClick={() => setTab("articles")} className="rounded-full px-5" data-testid="admin-tab-articles">
            Articles ({posts.length})
          </Button>
          <Button variant={tab === "messages" ? "default" : "outline"} onClick={() => setTab("messages")} className="rounded-full px-5" data-testid="admin-tab-messages">
            Messages ({messages.length})
          </Button>
          <Button variant={tab === "abonnes" ? "default" : "outline"} onClick={() => setTab("abonnes")} className="rounded-full px-5" data-testid="admin-tab-subscribers">
            <Users className="h-4 w-4" /> Abonnés ({subscribers.length})
          </Button>
        </div>

        {tab === "articles" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-4" data-testid="admin-articles-list">
            {posts.length === 0 && <p className="text-muted-foreground">Aucun article pour le moment.</p>}
            {posts.map((post) => (
              <div key={post.id} className="flex items-center justify-between gap-4 rounded-2xl border border-border/70 bg-card/60 p-5" data-testid={`admin-article-${post.slug}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">{post.category}</p>
                  <h3 className="mt-1 truncate font-display text-lg font-semibold">{post.title}</h3>
                  <p className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(new Date(post.published_at))}</span>
                    <span>{post.read_time}</span>
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button asChild variant="outline" size="icon" className="rounded-xl" data-testid={`admin-edit-${post.slug}`}>
                    <Link to={`/admin/blog/${post.id}`}><Pencil className="h-4 w-4" /></Link>
                  </Button>
                  <Button variant="outline" size="icon" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => deletePost(post.id)} data-testid={`admin-delete-${post.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {tab === "messages" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && <p className="text-muted-foreground">Aucun message reçu.</p>}
            {messages.map((msg) => (
              <div key={msg.id} className="rounded-2xl border border-border/70 bg-card/60 p-5" data-testid={`admin-message-${msg.id}`}>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="font-semibold text-foreground">{msg.name}</span>
                  <a href={`mailto:${msg.email}`} className="inline-flex items-center gap-1 text-primary hover:text-foreground transition-colors"><Mail className="h-3 w-3" />{msg.email}</a>
                  {msg.company && <span className="text-muted-foreground">({msg.company})</span>}
                  {msg.service && <span className="rounded-full border border-primary/30 px-2 py-0.5 text-xs text-primary">{msg.service}</span>}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{msg.message}</p>
                <p className="mt-3 text-xs text-muted-foreground">{new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(msg.created_at))}</p>
              </div>
            ))}
          </motion.div>
        )}

        {tab === "abonnes" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8 space-y-3" data-testid="admin-subscribers-list">
            <div className="flex justify-end">
              <Button variant="outline" onClick={exportSubscribers} className="rounded-full px-5" disabled={subscribers.length === 0} data-testid="admin-export-subscribers-btn">
                <Download className="h-4 w-4" /> Exporter CSV
              </Button>
            </div>
            {subscribers.length === 0 && <p className="text-muted-foreground">Aucun abonné pour le moment.</p>}
            {subscribers.map((sub) => (
              <div key={sub.id} className="flex items-center justify-between gap-4 rounded-2xl border border-border/70 bg-card/60 px-5 py-4" data-testid={`admin-subscriber-${sub.id}`}>
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-primary" />
                  <a href={`mailto:${sub.email}`} className="font-medium text-foreground hover:text-primary transition-colors">{sub.email}</a>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-muted-foreground">
                    {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(new Date(sub.subscribed_at))}
                  </span>
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-xl text-destructive hover:bg-destructive/10" onClick={() => deleteSubscriber(sub.id)} data-testid={`admin-delete-subscriber-${sub.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
